import React from "react";

/**
 * Uniwersalny pager do arkusza maturalnego.
 *
 * Props:
 * - position: "top" | "bottom"
 * - currentPage: number
 * - pages: Array<{ tasks: any[], totalPoints: number }>
 * - pageDone: boolean[]
 * - onChangePage: (pageIndex: number) => void
 * - onTogglePageDone: (pageIndex: number) => void
 *
 * (opcjonalnie, do czasu + pauzy):
 * - showTime?: boolean
 * - elapsedSeconds?: number
 * - grossSeconds?: number
 * - startedAt?: Date | string
 * - paused?: boolean
 * - onTogglePause?: () => void
 */

export default function MaturaExamPager({
  position = "top",
  currentPage,
  pages,
  pageDone,
  onChangePage,
  onTogglePageDone,
  showTime = position === "top",
  elapsedSeconds,
  grossSeconds,
  startedAt,
  paused,
  onTogglePause,
  compact = false,
}) {
  const totalPages = pages?.length || 0;
  const page = pages && pages[currentPage];

  const handlePrev = () => {
    if (currentPage <= 0) return;
    onChangePage(currentPage - 1);
  };

  const handleNext = () => {
    if (currentPage >= totalPages - 1) return;
    onChangePage(currentPage + 1);
  };

  const handleToggleDone = () => {
    onTogglePageDone(currentPage);
  };

  const formatTime = (seconds) => {
    if (!Number.isFinite(seconds) || seconds < 0) seconds = 0;
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    const pad = (v) => (v < 10 ? `0${v}` : `${v}`);
    if (h > 0) return `${h}:${pad(m)}:${pad(s)}`;
    return `${m}:${pad(s)}`;
  };

  const formatStartedAt = (value) => {
    if (!value) return "";
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString("pl-PL", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const showTimerBlock = showTime && typeof elapsedSeconds === "number";
  const pauseLabel = paused ? "Kontynuuj" : "Pauza i zapisz";

  if (!page) {
    return null;
  }

  return (
    <div
      style={{
        marginBottom: position === "top" ? (compact ? 8 : 16) : 0,
        marginTop: position === "bottom" ? (compact ? 8 : 16) : 0,
        padding: compact ? "8px 10px" : 12,
        borderRadius: 10,
        border: "1px solid var(--color-border-subtle)",
        background: "var(--color-elevated-bg)",
        display: "flex",
        flexDirection: "column",
        gap: compact ? 6 : 10,
      }}
    >
      {/* górna linia: Poprzednia – info – Następna */}
      <div
        style={{
          display: "flex",
          alignItems: "stretch",
          gap: compact ? 8 : 12,
          flexWrap: "wrap",
        }}
      >
        {/* lewa strona – przycisk Poprzednia */}
        <div style={{ flexShrink: 0 }}>
          <button
            type="button"
            className="btn-secondary"
            onClick={handlePrev}
            disabled={currentPage === 0}
            style={compact ? { padding: "5px 10px", fontSize: 13 } : undefined}
          >
            ◀ Poprzednia
          </button>
        </div>

        {/* środek – info o stronie + czas + pauza */}
        <div
          style={{
            flex: 1,
            minWidth: 220,
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            gap: 4,
          }}
        >
          <div style={{ fontSize: 14, opacity: 0.85 }}>
            Strona{" "}
            <strong>
              {currentPage + 1} / {totalPages}
            </strong>{" "}
            – punkty na tej stronie: <strong>{page.totalPoints}</strong>
          </div>

          {showTimerBlock && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 4,
                fontSize: 14,
              }}
            >
              {/* Linia z czasem – zawsze jedna linijka (czas + brutto) */}
              <div
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  justifyContent: "center",
                  gap: 6,
                  flexWrap: "nowrap",
                }}
              >
                <span>
                  Czas roboczy:{" "}
                  <strong>{formatTime(elapsedSeconds)}</strong>
                </span>
                {typeof grossSeconds === "number" && grossSeconds >= 0 && (
                  <span
                    style={{
                      fontSize: 12,
                      opacity: 0.8,
                    }}
                  >
                    (brutto: {formatTime(grossSeconds)})
                  </span>
                )}
              </div>

              {/* Nowa linia – godzina rozpoczęcia */}
              {startedAt && (
                <div
                  style={{
                    fontSize: 12,
                    opacity: 0.8,
                  }}
                >
                  rozpoczęcie: {formatStartedAt(startedAt)}
                </div>
              )}

              {/* Druga linia – tylko przycisk pauzy */}
              {typeof onTogglePause === "function" && (
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={onTogglePause}
                >
                  {pauseLabel}
                </button>
              )}
            </div>
          )}
        </div>

        {/* prawa strona – przycisk Następna */}
        <div style={{ flexShrink: 0 }}>
          <button
            type="button"
            className="btn-secondary"
            onClick={handleNext}
            disabled={currentPage === totalPages - 1}
            style={compact ? { padding: "5px 10px", fontSize: 13 } : undefined}
          >
            Następna ▶
          </button>
        </div>
      </div>

      {/* numerki stron – kółeczka */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          gap: 6,
        }}
      >
        {pages.map((_, idx) => {
          const isCurrent = idx === currentPage;
          const isDone = !!pageDone[idx];

          const bg = isCurrent
            ? "var(--color-accent)"
            : isDone
            ? "var(--color-success-bg, #1e7f3b)"
            : "transparent";
          const color = isCurrent
            ? "var(--color-on-accent, #000)"
            : isDone
            ? "var(--color-success-text, #fff)"
            : "var(--color-text)";

          const borderColor = isCurrent
            ? "var(--color-accent)"
            : isDone
            ? "var(--color-success-bg, #1e7f3b)"
            : "var(--color-border-subtle)";

          return (
            <button
              key={idx}
              type="button"
              onClick={() => onChangePage(idx)}
              style={{
                width: 26,
                height: 26,
                borderRadius: "50%",
                border: `1px solid ${borderColor}`,
                background: bg,
                color,
                fontSize: 13,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                padding: 0,
              }}
            >
              {/* numer zawsze w środku, ptaszek mały w rogu */}
              <div
                style={{
                  position: "relative",
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <span>{idx + 1}</span>
                {isDone && (
                  <span
                    style={{
                      position: "absolute",
                      right: 3,
                      bottom: 2,
                      fontSize: 11,
                      lineHeight: 1,
                    }}
                  >
                    ✓
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* checkbox "strona ukończona" */}
      <div style={{ textAlign: "center", marginTop: compact ? 0 : 4 }}>
        <label style={{ fontSize: 13, cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={!!pageDone[currentPage]}
            onChange={handleToggleDone}
            style={{ marginRight: 6 }}
          />
          Odhacz tę stronę jako ukończoną
        </label>
      </div>
    </div>
  );
}
